import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableComponent } from '../../shared/components/table/table.component';
import { TableColumn } from '../../core/models/column.model';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { UpdateRecordService } from '../../core/services/update-record.service';
import { UpdateRecord } from '../../core/models/update-record.model';
import { ConfirmPopupComponent } from '../../shared/components/confirm-popup/confirm-popup.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { ModalComponent } from '../modal/modal.component';
import { Constants } from '../../shared/utils/constants';
import { ReactiveFormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-daily-shift-preview',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    RouterModule,
    MatDialogModule,
    MatSnackBarModule,
    ConfirmPopupComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './daily-shift-preview.component.html',
  styleUrls: ['./daily-shift-preview.component.scss'],
})
export class DailyShiftPreviewComponent implements OnInit {
  flattenedData = {
    seguridad: [] as any[],
    personal: [] as any[],
    dcs: [] as any[],
    salmuera_i: [] as any[],
    salmuera_ii: [] as any[],
    electrolizadores: [] as any[],
    subestacion: [] as any[],
    compresion: [] as any[],
    cloruro_ferrico: [] as any[],
    movimiento_producto: [] as any[],
    avisoAveria: [] as any[],
  };

  seguridadColumns: TableColumn[] = [
    { header: 'Hora', field: 'hora' },
    { header: 'Tipo', field: 'tipo_novedad' },
    { header: 'Contenido', field: 'novedad' },
  ];

  personalColumns: TableColumn[] = [
    { header: 'Hora', field: 'hora' },
    { header: 'Contenido', field: 'novedad' },
  ];

  areaColumns: TableColumn[] = [
    { header: 'Hora', field: 'hora' },
    { header: 'Contenido', field: 'detalleNovedad' },
  ];

  avisoAveriaColumns: TableColumn[] = [
    { header: 'Hora', field: 'hora' },
    { header: 'Área', field: 'updateArea' },
    { header: 'Prioridad', field: 'prioridad' },
    { header: '¿Qué está mal?', field: 'que_esta_mal' },
    { header: '¿Qué tiene de malo?', field: 'que_tiene_de_malo' },
    { header: 'Debería', field: 'deberia' },
  ];

  recordToClone: any | null = null;
  showClonePopup = false;

  constructor(
    private updateRecordService: UpdateRecordService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Cargar “Seguridad” y “Personal”
    this.loadUpdatesByType(Constants.SEGURIDAD, 'seguridad');
    this.loadUpdatesByType(Constants.PERSONAL, 'personal');

    // Cargar cada área
    this.loadArea(Constants.DCS, 'dcs', Constants.AVISO_AVERIAS);
    this.loadArea(Constants.SALMUERA_I, 'salmuera_i', Constants.AVISO_AVERIAS);
    this.loadArea(Constants.SALMUERA_II, 'salmuera_ii', Constants.AVISO_AVERIAS);
    this.loadArea(Constants.ELECTROLIZADORES, 'electrolizadores', Constants.AVISO_AVERIAS);
    this.loadArea(Constants.SUBESTACION, 'subestacion', Constants.AVISO_AVERIAS);
    this.loadArea(Constants.COMPRESION, 'compresion', Constants.AVISO_AVERIAS);
    this.loadArea(Constants.CLORURO_FERRICO, 'cloruro_ferrico', Constants.AVISO_AVERIAS);
    this.loadArea(Constants.MOVIMIENTO_PRODUCTO, 'movimiento_producto', Constants.AVISO_AVERIAS);

    // Cargar “Aviso de Avería”
    this.loadUpdatesByType(Constants.AVISO_AVERIAS, 'avisoAveria');
  }

  private loadUpdatesByType(
    type: string,
    targetKey: keyof typeof this.flattenedData,
    isClosed = false,
    isMarkedForClosure = true,
    isMarkedForPreClosure?: boolean,
  ): void {
    this.updateRecordService
      .getUpdateRecordsByTypeAndAreas(
        type,
        [
          Constants.GENERAL,
          Constants.DCS,
          Constants.SALMUERA_I,
          Constants.SALMUERA_II,
          Constants.ELECTROLIZADORES,
          Constants.SUBESTACION,
          Constants.COMPRESION,
          Constants.CLORURO_FERRICO,
          Constants.MOVIMIENTO_PRODUCTO,
        ],
        isClosed,
        isMarkedForClosure,
        isMarkedForPreClosure,
      )
      .subscribe({
        next: updates => {
          this.flattenedData[targetKey] = this.flattenData(updates);
        },
        error: err => {
          console.error(`Error al cargar ${type}:`, err);
          this.flattenedData[targetKey] = [];
        },
      });
  }

  private loadArea(areaConstant: string, localKey: keyof typeof this.flattenedData, excludedUpdateTypeCode?: string) {
    this.updateRecordService
      .getUpdateRecordsByAreas([areaConstant], false, true, undefined)
      .subscribe({
        next: updates => {
          let filtered = updates;
          if (excludedUpdateTypeCode) {
            filtered = updates.filter(u =>
              u.updateType?.code?.toLowerCase() !== excludedUpdateTypeCode.toLowerCase()
            );
          }

          this.flattenedData[localKey] = this.flattenData(filtered);
        },
        error: err => {
          console.error(`Error al cargar área ${areaConstant}:`, err);
          this.flattenedData[localKey] = [];
        },
      });
  }

  // Determina la marca de cierre a mostrar según el área del registro
  private getRoleSpecificClosureMark(update: UpdateRecord): boolean {
    const u: any = update as any;
    const areaCode = update.updateArea?.code?.toLowerCase?.() ?? '';
    if (areaCode === 'dcs') {
      return (u.isMarkedForClosureDCS ?? u.isMarkedForClosureDcs ?? update.isMarkedForClosure) as boolean;
    }
    if (areaCode === 'cloruro_ferrico' || areaCode === 'ferrico') {
      return (u.isMarkedForClosureFerrico ?? update.isMarkedForClosure) as boolean;
    }
    return update.isMarkedForClosure;
  }

  flattenData(updates: UpdateRecord[]): any[] {
    return updates.flatMap(update => {
      const elements = update.updateElements;
      const getElementValue = (label: string): string =>
        elements.find(el => el.label === label)?.updateValues?.[0]?.value || '';

      const hora = getElementValue('Hora');
      const novedad = getElementValue('Novedad');
      const tipoNovedad = getElementValue('Tipo de Novedad');
      const prioridad = getElementValue('Prioridad');
      const queEstaMal = getElementValue('¿Qué está mal?');
      const queTieneDeMalo = getElementValue('¿Qué tiene de malo?');
      const deberia = getElementValue('Debería');
      const nOrden = getElementValue('N° Orden de Trabajo');
      const horaInicio = getElementValue('Hora Inicio');
      const horaTermino = getElementValue('Hora Término');
      const horaPermiso = getElementValue('Hora Permiso Otorgado');
      const enEjecucion = getElementValue('En Ejecución');
      const comentarios = getElementValue('Comentarios');
      const horaFueraServicio = getElementValue('Hora Fuera de Servicio');
      const ingreseNovedad = getElementValue('Ingrese Novedad');

      const detalleNovedad = elements
        .filter(el => !(el.label === 'Hora' || el.element.name === 'Label') && el.label !== 'Tipo de Novedad')  
        .map(el => {
          const values = el.updateValues?.map(val => `${el.label}: ${val.value}`).join(', ') || '';
          return values || `${el.label}: N/A`;
        })
        .filter(txt => txt.trim() !== '')
        .join(', ');

      return {
        id: update.id,
        hora: hora ? hora : horaInicio,
        hora_inicio: horaInicio,
        hora_termino: horaTermino,
        hora_permiso: horaPermiso,
        hora_fuera_servicio: horaFueraServicio,
        ingrese_novedad: ingreseNovedad,
        en_ejecucion: enEjecucion,
        n_orden: nOrden,
        prioridad,
        que_esta_mal: queEstaMal,
        que_tiene_de_malo: queTieneDeMalo,
        deberia,
        responsable:
          elements.find(el => el.element?.id === 2 || el.element?.id === 5)?.updateValues?.[0]
            ?.createdUser?.username || '',
        tipo_novedad: tipoNovedad ? tipoNovedad : update.updateType?.name,
        tipo: update.updateType?.name,
        area: update.updateArea?.name,
        updateArea: update.updateArea?.name,
        novedad: novedad,
        detalleNovedad,
        comentarios,
        fecha:
          elements.find(el => el.element?.id === 2 || el.element?.id === 5)?.updateValues?.[0]
            ?.createdUtc || '',
        updateElements: elements,
        updateType: update.updateType,
        isMarkedForClosure: this.getRoleSpecificClosureMark(update),
        isMarkedForPreClosure: update.isMarkedForPreClosure,
        isCloned: update.isCloned,
      };
    });
  }

  viewUpdateRecord(row: any): void {
    const dialogRef = this.dialog.open(ModalComponent, {
      data: { record: row, readOnly: true },
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  openCloneConfirmation(row: any): void {
    this.recordToClone = row;
    this.showClonePopup = true;
  }

  closeClonePopup(): void {
    this.showClonePopup = false;
    this.recordToClone = null;
  }

  confirmClone(): void {
    if (!this.recordToClone) {
      console.error('No hay registro para clonar.');
      return;
    }
    this.updateRecordService.cloneUpdateRecord(this.recordToClone.id).subscribe({
      next: clonedRecord => {
        console.log('Registro clonado exitosamente:', clonedRecord);
        // Actualizamos
        forkJoin({
          originalUpdate: this.updateRecordService.updateMarkForClosure(
            this.recordToClone.id,
            false,
          ),
          clonedUpdate: this.updateRecordService.updateMarkForClosure(clonedRecord.id, true),
        }).subscribe({
          next: () => {
            console.log('Estados (original y clon) actualizados.');
            this.ngOnInit();
            this.closeClonePopup();
          },
          error: err => {
            console.error('Error al actualizar los estados:', err);
            this.snackBar.open('Error al actualizar los estados.', 'Cerrar', { duration: 3000 });
          },
        });
      },
      error: err => {
        console.error('Error al clonar el registro:', err);
        this.snackBar.open('Error al clonar.', 'Cerrar', { duration: 3000 });
      },
    });
  }

  editUpdateRecord(row: any): void {
    if (!row.updateElements) {
      console.error('El objeto no tiene updateElements:', row);
      return;
    }
    const dialogRef = this.dialog.open(ModalComponent, {
      data: { record: row, readOnly: false },
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        console.log('Edición realizada con éxito.');
        this.ngOnInit();
      }
    });
  }

  deleteUpdateRecord(id: number): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: '¿Seguro que deseas eliminar este registro?' },
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.updateRecordService.deleteUpdateRecord(id).subscribe({
          next: () => {
            console.log(`Eliminado ID ${id} ok.`);
            this.ngOnInit();
          },
          error: err => {
            console.error(`Error al eliminar ID ${id}:`, err);
          },
        });
      }
    });
  }
}
